import React from 'react';
import { useTranslation } from 'next-i18next';

import Box from '@mui/material/Box';

import Dialog from 'src/components/ui/Dialog';
import IconButton from 'src/components/ui/IconButton';
import { breakpoints } from 'src/constants/breakpoints';
import { styledComponent } from 'src/utils/styled';
import { QuickSignupForm, type QuickSignupFormValues } from './QuickSignupForm';
import useLoginModal from 'src/hooks/useLoginModal';
import Typography from 'src/components/ui/Typography';
import LinkButton from 'src/components/ui/LinkButton';
import type { AuthProvider } from 'src/hooks/useAuthActions';
import useAuthActions from 'src/hooks/useAuthActions';
import { useManagedRouter } from 'src/hooks/useAppRouter';
import type { icons } from 'src/components/ui/Icon';
import Icon from 'src/components/ui/Icon';
import { colours } from 'src/constants/colours';
import Divider from 'src/components/ui/Divider';

const StyledDialog = styledComponent(Dialog)`
    display: flex;
    flex-direction: column;
    align-items: center;
    .MuiDialog-container {
        @media (max-width: ${breakpoints.tabletPortrait - 1}px) {
            width: 100%;
        }
    }
    .MuiDialog-paper {
        margin: auto;
        max-width: 42rem;
        @media (max-width: ${breakpoints.tabletPortrait - 1}px) {
            width: 100%;
            height: 100%;
            max-width: none;
        }
    }
    .MuiDialog-paperScrollPaper {
        @media (max-width: ${breakpoints.tabletPortrait - 1}px) {
            max-height: calc(100% - 0rem);
        }
    }
`;

const CloseButton = styledComponent(IconButton)`
    position: absolute;
    top: 1rem;
    right: 1rem;
`;

const StyledBox = styledComponent(Box)`
    min-width: 39rem;
    @media (max-width: ${breakpoints.tabletPortrait - 1}px) {
        min-width: 0rem;
    }
`;

type UserPrefilledInfos = {
    email?: string;
    firstName?: string;
    lastName?: string;
};

type Props = {
    open: boolean;
    onClose: () => void;
    userPrefilledInfos?: UserPrefilledInfos;
};

export const QuickSignupModal: React.FC<Props> = ({ open, onClose, userPrefilledInfos }) => {
    const { router, removeQueryParams } = useManagedRouter();
    const { t } = useTranslation(['common']);
    const { createAuthenticatedHandler } = useLoginModal();
    const { quickSignupUpSIV, signUpSocial } = useAuthActions();

    const handleClose = async () => {
        // Remove query params siv, e, f or n
        const newQuery = removeQueryParams(['siv', 'e', 'f', 'n']);
        await router.replace({ query: newQuery }, undefined, { shallow: true });
        onClose();
    };

    const handleSubmitForm = async (values: QuickSignupFormValues) => {
        const isSuccess = await quickSignupUpSIV({
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
            password: values.password,
            hasOptedInToMarketingComms: values.hasOptedInToMarketingComms,
        });
        if (isSuccess) {
            await handleClose();
        }
    };

    const handleAuthProviderClick = async (provider: AuthProvider) => {
        const { siv, n, f, e, ...query } = router.query;
        const params = new URLSearchParams(query as Record<string, string>);
        const status = await signUpSocial({
            provider,
            skipRedirectAfterLogin: true,
            quickSignupSiv: true,
            returnUrlAfterSignup: `${router.pathname}?${params.toString()}`,
        });
        if (status === 'login') {
            // If the user is logged in, close the modal and remove siv query params
            await handleClose();
        } else if (status === 'signup') {
            // If the user is signed up, close the modal, don't need to remove siv query params because user is redirected
            onClose();
        }
    };

    return (
        <StyledDialog data-target-id="signup-modal" open={open} onClose={handleClose}>
            <CloseButton
                color="white"
                onClick={handleClose}
                iconProps={{
                    name: 'Close',
                }}
            />
            <StyledBox p={6}>
                <Typography variant="h2" data-target="quick-signup-modal-title">
                    {t('common:signUp.modal.title')}
                </Typography>
                <Typography
                    marginTop="0.5rem"
                    data-target="quick-signup-modal-description"
                    color={colours.primary.main}
                >
                    {t('common:signUp.modal.description')}
                </Typography>
                <Box display="flex" flexDirection="column" gap="0.6rem" marginTop="0.5rem">
                    <FeaturePresentationRow
                        icon="BookmarkPrimaryInactive"
                        text={t('common:signUp.modal.adsFeatureDescription')}
                    />
                    <FeaturePresentationRow
                        icon="ChatPrimaryInactive"
                        text={t('common:signUp.modal.agentsFeatureDescription')}
                    />
                    <FeaturePresentationRow
                        icon="NotificationBlackInactive"
                        text={t('common:signUp.modal.notificationsFeatureDescription')}
                    />
                </Box>
                <SignupProviders onAuthProviderClick={handleAuthProviderClick} />
                <Box marginTop="2rem" marginBottom="1.5rem">
                    <Divider label={t('common:signIn.or')} />
                </Box>
                <QuickSignupForm prefilledInfos={userPrefilledInfos} onSubmit={handleSubmitForm} />
                <Box display="flex" flexWrap="wrap" flexDirection="row" marginTop="1.5rem">
                    <Typography variant="body1">{t('common:signUp.signInQuestion')}</Typography>
                    &nbsp;
                    <LinkButton
                        onClick={async () => {
                            await handleClose();
                            // eslint-disable-next-line @typescript-eslint/no-floating-promises
                            createAuthenticatedHandler(undefined, undefined, undefined, undefined, 'login');
                        }}
                    >
                        {t('common:signIn.title')}
                    </LinkButton>
                </Box>
            </StyledBox>
        </StyledDialog>
    );
};

type FeaturePresentationRowProps = {
    icon: keyof typeof icons;
    text: string;
};
const FeaturePresentationRow: React.FC<FeaturePresentationRowProps> = ({ icon, text }) => (
    <Box display="flex">
        <Box marginRight="0.5rem">
            <Icon name={icon} />
        </Box>
        <Typography variant="body2" color={colours.primary.main}>
            {text}
        </Typography>
    </Box>
);

type SignupProvidersProps = {
    onAuthProviderClick: (provider: AuthProvider) => void;
};
const SignupProviders: React.FC<SignupProvidersProps> = ({ onAuthProviderClick }) => (
    <Box display="flex" justifyContent="center" gap="2.8rem" marginTop="2rem">
        <IconButton iconProps={{ name: 'Facebook', size: '2.3rem' }} onClick={() => onAuthProviderClick('facebook')} />
        <IconButton iconProps={{ name: 'Google', size: '2.3rem' }} onClick={() => onAuthProviderClick('google')} />
        <IconButton iconProps={{ name: 'Apple', size: '2.3rem' }} onClick={() => onAuthProviderClick('apple')} />
    </Box>
);
